/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useCallback} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Chakra Components
import {
    Box,
    Checkbox,
    Flex,
    Heading,
    HStack,
    Stack,
    Text
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import DisplayPrice from '@app/components/display-price'
import ItemVariantProvider from '../../../components/item-variant'
import CartItemVariantImage from '../../../components/item-variant/item-image'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {HideOnDesktop, HideOnMobile} from '@salesforce/retail-react-app/app/components/responsive'
import {CustomCheckboxIcon} from '../../../components/field'

// Utilities
import {noop} from '@salesforce/retail-react-app/app/utils/utils'

// Hooks
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import FieldsetForm from '../../../components/shared/ui/FieldsetForm'

/**
 * A compound component that returns a checkboxs that allows the user to select if they want a
 * gift with purchase product based on the applied promotion.
 *
 * @param {*} onIsGiftWithPurchaseChange method to notify components parent when the checkbox is selected or deselected.
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const GiftWithPurchaseCheckbox = ({isChecked, setIsChecked}) => {
    const {formatMessage} = useIntl()
    return (
        <Stack bgColor={'lightBeige'} mt={16} p={'12px 16px'}>
            <FieldsetForm
                isMandatoryNote={false}
                legendText={formatMessage({
                    defaultMessage: 'Gift with purchase',
                    id: 'gift_with_purchase.form.legend'
                })}
            >
                <Checkbox
                    icon={<CustomCheckboxIcon />}
                    iconSize="0.1rem"
                    name={`gift-with-purchase-checkbox`}
                    spacing={8}
                    isChecked={isChecked}
                    onChange={(e) => {
                        const checked = e.target.checked
                        setIsChecked(checked)
                    }}
                >
                    <FormattedMessage
                        defaultMessage="Yes, I’d like to receive this gift"
                        id="gift_with_purchase.label.checkbox"
                    />
                </Checkbox>
            </FieldsetForm>
            {!isChecked && (
                <Text variant={'bodySmall'} mt={4}>
                    <FormattedMessage
                        defaultMessage="You have opted out from your Bonus Product."
                        id="gift_with_purchase.label.checkbox.description"
                    />
                </Text>
            )}
        </Stack>
    )
}

/**
 * Create a special GiftWithPurchase component to display a Product in the `cart`
 * that has been added as a gift from a promotion that can be toggled by the user for confirmation
 *
 * @param {*} {product, onIsGiftWithPurchaseChange = noop, showLoading = false}
 * @returns A JSX element representing product item in a list (eg: wishlist, cart, etc).
 */
const GiftWithPurchase = ({product, onIsGiftWithPurchaseChange = noop, showLoading = false}) => {
    const {currency: activeCurrency} = useCurrency()
    const [isChecked, setIsChecked] = useState(product.c_sendThisGift ?? true)
    const {formatMessage} = useIntl()
    const handleIsGiftWithPurchaseChange = useCallback(
        (checked, product) => {
            onIsGiftWithPurchaseChange(checked, product)
        },
        [onIsGiftWithPurchaseChange]
    )

    // Notify parent to handle out of stock product
    useEffect(() => {
        handleIsGiftWithPurchaseChange(isChecked, product)
    }, [isChecked])

    // The first time the component is mount, if "c_sendThisGift" is not present in the product,
    // we will trigger this call with the default value true.
    // We do it because it's not possible to define this field as true by default in the BM
    useEffect(() => {
        if (product && !('c_sendThisGift' in product)) {
            handleIsGiftWithPurchaseChange(true, product)
        }
    }, [])

    if (!product) {
        return null
    }
    return (
        <Box
            position="relative"
            data-testid={`sf-cart-item-${product.productId ? product.productId : product.id}`}
            role="group"
            aria-label={formatMessage({
                defaultMessage: 'Bonus product',
                id: 'gift_with_purchase.label.bonus'
            })}
            p={['24px 16px', null, '32px 40px']}
            bg="white"
            border="1px solid"
            borderColor={'borderGray'}
        >
            <ItemVariantProvider variant={product}>
                {showLoading && <LoadingSpinner />}
                <Flex width="full" alignItems="flex-start" align="flex-start">
                    <CartItemVariantImage
                        width={['85px', null, '147px']}
                        mr={[16, null, 24]}
                        opacity={isChecked ? 1 : 0.5}
                    />
                    <Box flex={1}>
                        <Flex
                            align={['flex-start', null, 'flex-end']}
                            direction={['column', null, 'row']}
                            justify="space-between"
                            opacity={isChecked ? 1 : 0.5}
                        >
                            <Stack spacing={[8, null, 4]}>
                                <Text variant="eyeBrowSmall" textTransform={'uppercase'}>
                                    <FormattedMessage
                                        defaultMessage="Bonus product"
                                        id="gift_with_purchase.label.bonus"
                                    />
                                </Text>
                                <Heading as="h2" size="3xl" textTransform="uppercase">
                                    {product.productName || product.name}
                                </Heading>
                            </Stack>
                            <HStack
                                marginTop={2}
                                mt={[8, null, 0]}
                                mb={[0, null, 4]}
                                lineHeight="16px"
                            >
                                <DisplayPrice
                                    labelForA11y={product?.name}
                                    currency={activeCurrency}
                                    priceData={{
                                        currentPrice: product.price,
                                        listPrice: null,
                                        pricePerUnit: product?.pricePerUnit,
                                        isASet: product?.type?.set,
                                        isMaster: product?.type?.master,
                                        isRange:
                                            product?.type?.set || product?.type?.master || false,
                                        isOnSale: product?.price > product?.priceAfterItemDiscount
                                    }}
                                    currentPriceProps={{
                                        variant: 'bodyLarge1',
                                        color: 'darkGray',
                                        textDecoration: 'line-through'
                                    }}
                                />
                                <Text variant={'bodyLarge1'}>
                                    <FormattedMessage
                                        defaultMessage="FREE"
                                        id="gift_with_purchase.label.free"
                                    />
                                </Text>
                            </HStack>
                        </Flex>
                        <HideOnMobile>
                            <GiftWithPurchaseCheckbox
                                isChecked={isChecked}
                                setIsChecked={setIsChecked}
                            />
                        </HideOnMobile>
                    </Box>
                </Flex>
                <HideOnDesktop>
                    <GiftWithPurchaseCheckbox isChecked={isChecked} setIsChecked={setIsChecked} />
                </HideOnDesktop>
            </ItemVariantProvider>
        </Box>
    )
}

GiftWithPurchase.propTypes = {
    product: PropTypes.object,
    onIsGiftWithPurchaseChange: PropTypes.func,
    showLoading: PropTypes.bool
}

GiftWithPurchaseCheckbox.propTypes = {
    isChecked: PropTypes.bool,
    setIsChecked: PropTypes.func
}

export default GiftWithPurchase
